/**
 * Query and mutation hooks, one place.
 *
 * *** QUERY KEYS ARE STRUCTURED, NOT STRINGS ***
 * `['mentors', 'list', filters]` lets a mutation invalidate every mentor query
 * with `['mentors']` — list and detail together — without naming each one.
 * Flat string keys drift, and then a create succeeds while the list still shows
 * the old data.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from './api'
import { useAuth } from './auth'
import type {
  AdminAccountList,
  CreateAdminInput,
  CreatedAdminAccount,
  AvailableDates,
  BookingList,
  CancellationPreview,
  MyBooking,
  MyBookingList,
  ChatMessage,
  ChatMessageList,
  ChatQuota,
  ChatThreadList,
  AffectedBookings,
  BreakEntry,
  Breaks,
  CancelBookingsResult,
  Feedback,
  FeedbackInput,
  OneOffBreak,
  OneOffBreaks,
  PublicCategory,
  PublicCategoryList,
  SubscriptionHistory,
  Vacations,
  WorkingHours,
  WorkingHoursEntry,
  MentorProfileUpdate,
  MyMentorProfile,
  MyStudentProfile,
  MySubscription,
  PaymentIntent,
  PublicPlanList,
  PurchaseStarted,
  PriceQuote,
  PublicMentorList,
  PublicSessionTypeList,
  SlotList,
  CashRequestList,
  Category,
  CategoryInput,
  CategoryList,
  AdminLibraryList,
  LibraryCardList,
  LibraryInput,
  LibraryMentorGrantList,
  LibraryLeaveResult,
  LibraryOccupantList,
  Leaderboard,
  LeaderboardPeriod,
  PersonCard,
  MyProgress,
  RoomMessage,
  RoomMessageList,
  LibraryVisit,
  ChatOverview,
  ChatPolicy,
  ChatPolicyInput,
  Dashboard,
  FeeHead,
  FeeHeadInput,
  FeeHeadList,
  FeedbackList,
  Mentor,
  MentorCreated,
  MentorInput,
  MentorDeactivation,
  MentorList,
  MentorPerformanceList,
  MentorUpdateInput,
  PaymentList,
  PricingRule,
  PricingRuleInput,
  PricingRuleList,
  ScheduledBreaksResponse,
  SessionType,
  SessionTypeInput,
  SessionTypeList,
  StorageUsage,
  StudentDetail,
  StudentList,
  StudentSubscription,
  SubscriptionDetail,
  SubscriptionPlan,
  SubscriptionPlanInput,
  OperatingDayImpact,
  TenantSettings,
  TenantSettingsInput,
  Uuid,
} from '@/types/api'

export const keys = {
  dashboard: ['dashboard'] as const,
  subscriptionPlans: ['subscription-plans'] as const,
  cashRequests: ['cash-requests'] as const,
  studentSubscription: (id: string) => ['student-subscription', id] as const,
  mentorPerformance: (limit: number) => ['dashboard', 'mentors', limit] as const,

  categories: ['categories'] as const,
  libraries: ['libraries'] as const,
  leaderboard: ['library-leaderboard'] as const,
  adminLibraries: ['admin-libraries'] as const,
  sessionTypes: ['session-types'] as const,
  pricingRules: ['pricing-rules'] as const,
  feeHeads: ['fee-heads'] as const,
  chatPolicy: ['chat-policy'] as const,

  mentors: ['mentors'] as const,
  mentorBreaks: ['mentor-breaks'] as const,
  mentor: (id: Uuid) => ['mentors', id] as const,

  students: (filters: unknown) => ['students', 'list', filters] as const,
  student: (id: Uuid) => ['students', id] as const,

  bookings: (filters: unknown) => ['bookings', 'list', filters] as const,

  /*
    *** SEPARATE FROM `bookings` ON PURPOSE ***
    `bookings` is the admin table — every session in the tenant. These are
    the caller's OWN sessions from `/bookings`, a different endpoint with a
    different shape. One key for both would serve an admin's table rows to
    a student's screen.
  */
  myBookings: (upcoming: boolean) => ['my-bookings', upcoming] as const,

  /*
    The STUDENT's own plan, separate from the admin `subscriptionPlans`
    key for the same reason `myBookings` is separate from `bookings`.
  */
  mySubscription: ['my-subscription'] as const,
  mySubscriptionHistory: ['my-subscription', 'history'] as const,
  myPlans: ['my-plans'] as const,

  /* A mentor's own diary. The admin equivalents live under `mentors`. */
  myWorkingHours: ['my-availability', 'hours'] as const,
  myBreaks: ['my-availability', 'breaks'] as const,
  myOneOffBreaks: ['my-availability', 'one-off'] as const,
  myVacations: ['my-availability', 'vacations'] as const,
  affectedBookings: (range: unknown) =>
    ['my-availability', 'affected', range] as const,

  /* Subject areas a STUDENT may pick. `categories` is the admin list. */
  publicCategories: ['public-categories'] as const,

  /* The caller's own profile. `students`/`mentors` are the admin tables. */
  myStudentProfile: ['my-profile', 'student'] as const,
  myMentorProfile: ['my-profile', 'mentor'] as const,

  /*
    The caller's OWN conversations. `chatThreads` is the admin table.

    *** THE VIEWER IS PART OF THE KEY ***
    `is_mine` and `is_locked` are computed per caller, so the SAME thread id
    means different data to a student and to a mentor. Without the viewer in
    the key, signing out and back in as the other party served the previous
    account's page: every bubble on the wrong side until the 10-second poll
    replaced it (reported 2026-11-02).

    Clearing the cache on logout fixes the common case; this makes the key
    honest, which also covers a token refresh that lands as someone else.
  */
  myThreads: (viewerId: string) => ['my-threads', viewerId] as const,
  threadMessages: (viewerId: string, id: Uuid) =>
    ['my-threads', viewerId, 'messages', id] as const,
  threadQuota: (viewerId: string, id: Uuid) =>
    ['my-threads', viewerId, 'quota', id] as const,

  myBooking: (id: Uuid) => ['my-bookings', 'detail', id] as const,
  cancellationPreview: (id: Uuid) =>
    ['my-bookings', 'cancel-preview', id] as const,
  payments: (filters: unknown) => ['payments', 'list', filters] as const,
  feedback: (filters: unknown) => ['feedback', 'list', filters] as const,
  chatThreads: (filters: unknown) => ['chat-threads', filters] as const,

  admins: ['admins'] as const,

  settings: ['settings'] as const,
  operatingDayImpact: (weekday: number) =>
    ['operating-day-impact', weekday] as const,
  storage: ['storage'] as const,
}

// ------------------------------------------------------------- dashboard
export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard,
    queryFn: () => api.get<Dashboard>('/admin/dashboard'),
  })
}

export function useMentorPerformance(limit = 10) {
  return useQuery({
    queryKey: keys.mentorPerformance(limit),
    queryFn: () =>
      api.get<MentorPerformanceList>('/admin/dashboard/mentors', { limit }),
  })
}

// -------------------------------------------------------------- categories
export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: () => api.get<CategoryList>('/admin/categories'),
    /*
      Categories change rarely and are needed by the mentor form, the pricing
      form, and the student filter. A longer stale time avoids refetching the
      same short list on every screen change.
    */
    staleTime: 5 * 60_000,
  })
}

export function useCreateCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: CategoryInput) =>
      api.post<Category>('/admin/categories', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.categories })
      // A new category changes the dashboard's counts too.
      client.invalidateQueries({ queryKey: keys.dashboard })
    },
  })
}

export function useUpdateCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: Uuid; input: Partial<CategoryInput> }) =>
      api.patch<Category>(`/admin/categories/${id}`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.categories }),
  })
}

export function useDeleteCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: Uuid) => api.delete<void>(`/admin/categories/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.categories })
      client.invalidateQueries({ queryKey: keys.dashboard })
    },
  })
}

// ------------------------------------------------------------ session types
export function useSessionTypes() {
  return useQuery({
    queryKey: keys.sessionTypes,
    queryFn: () => api.get<SessionTypeList>('/admin/session-types'),
    staleTime: 5 * 60_000,
  })
}

export function useCreateSessionType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: SessionTypeInput) =>
      api.post<SessionType>('/admin/session-types', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sessionTypes }),
  })
}

export function useUpdateSessionType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: Uuid
      input: Partial<SessionTypeInput>
    }) => api.patch<SessionType>(`/admin/session-types/${id}`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sessionTypes }),
  })
}

export function useDeleteSessionType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: Uuid) => api.delete<void>(`/admin/session-types/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sessionTypes }),
  })
}

// ----------------------------------------------------------- pricing rules
export function usePricingRules() {
  return useQuery({
    queryKey: keys.pricingRules,
    queryFn: () => api.get<PricingRuleList>('/admin/pricing-rules'),
  })
}

export function useCreatePricingRule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: PricingRuleInput) =>
      api.post<PricingRule>('/admin/pricing-rules', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.pricingRules }),
  })
}

export function useUpdatePricingRule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: Uuid
      input: Partial<PricingRuleInput>
    }) => api.patch<PricingRule>(`/admin/pricing-rules/${id}`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.pricingRules }),
  })
}

export function useDeletePricingRule() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: Uuid) => api.delete<void>(`/admin/pricing-rules/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.pricingRules }),
  })
}

// --------------------------------------------------------------- fee heads
export function useFeeHeads() {
  return useQuery({
    queryKey: keys.feeHeads,
    queryFn: () => api.get<FeeHeadList>('/admin/fee-heads'),
  })
}

export function useCreateFeeHead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: FeeHeadInput) =>
      api.post<FeeHead>('/admin/fee-heads', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.feeHeads }),
  })
}

export function useUpdateFeeHead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: Uuid; input: Partial<FeeHeadInput> }) =>
      api.patch<FeeHead>(`/admin/fee-heads/${id}`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.feeHeads }),
  })
}

export function useDeleteFeeHead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: Uuid) => api.delete<void>(`/admin/fee-heads/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.feeHeads }),
  })
}

// ------------------------------------------------------------- chat policy
export function useChatPolicy() {
  return useQuery({
    queryKey: keys.chatPolicy,
    queryFn: () => api.get<ChatPolicy>('/admin/chat-policy'),
  })
}

export function useUpdateChatPolicy() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: ChatPolicyInput) =>
      api.put<ChatPolicy>('/admin/chat-policy', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.chatPolicy }),
  })
}

// ----------------------------------------------------------------- mentors
/** Which mentors the admin list shows. Active unless asked otherwise. */
export type MentorFilter = 'active' | 'deactivated' | 'all'

export function useMentors(include: MentorFilter = 'active') {
  return useQuery({
    // *** THE FILTER IS PART OF THE KEY ***
    // Without it, switching to "Deactivated" would serve the cached active
    // list and look like the filter does nothing.
    queryKey: [...keys.mentors, include],
    queryFn: () =>
      api.get<MentorList>(`/admin/mentors?include=${include}`),
  })
}

export function useDeactivateMentor() {
  const client = useQueryClient()
  return useMutation({
    /*
      *** TWO-STEP BY DESIGN ***
      `cancel_upcoming: false` asks the question: a mentor with confirmed
      future sessions comes back 409 naming how many, which the page shows
      as a confirmation. `true` is the admin answering it, and those
      sessions are then cancelled — quota returned, students notified.
    */
    mutationFn: ({ id, cancelUpcoming }: { id: Uuid; cancelUpcoming: boolean }) =>
      api.post<MentorDeactivation>(`/admin/mentors/${id}/deactivate`, {
        cancel_upcoming: cancelUpcoming,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.mentors })
      client.invalidateQueries({ queryKey: keys.dashboard })
      // Their sessions may have been cancelled. `keys.bookings` takes a
      // filter argument, so the PREFIX is used — React Query matches keys
      // partially, which invalidates every filtered bookings list at once.
      client.invalidateQueries({ queryKey: ['bookings'] })
      // The mentor count per category changed.
      client.invalidateQueries({ queryKey: keys.categories })
    },
  })
}

export function useReactivateMentor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: Uuid) =>
      api.post<MentorDeactivation>(`/admin/mentors/${id}/reactivate`, {}),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.mentors })
      client.invalidateQueries({ queryKey: keys.dashboard })
      client.invalidateQueries({ queryKey: keys.categories })
    },
  })
}

/**
 * Upcoming time off across this client's mentors.
 *
 * Read-only: the API has no write route, deliberately. Availability is the
 * mentor's own statement about when they can work.
 */
export function useMentorBreaks() {
  return useQuery({
    queryKey: keys.mentorBreaks,
    queryFn: () =>
      api.get<ScheduledBreaksResponse>('/admin/mentor-breaks'),
  })
}

export function useMentor(id: Uuid | null) {
  return useQuery({
    queryKey: keys.mentor(id ?? ''),
    queryFn: () => api.get<Mentor>(`/admin/mentors/${id}`),
    enabled: id !== null,
  })
}

export function useCreateMentor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: MentorInput) =>
      api.post<MentorCreated>('/admin/mentors', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.mentors })
      client.invalidateQueries({ queryKey: keys.dashboard })
      // The mentor count per category changed.
      client.invalidateQueries({ queryKey: keys.categories })
    },
  })
}

export function useUpdateMentor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: Uuid; input: MentorUpdateInput }) =>
      api.patch<Mentor>(`/admin/mentors/${id}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.mentors })
      client.invalidateQueries({ queryKey: keys.categories })
    },
  })
}

export function useResetMentorPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: Uuid) =>
      api.post<MentorCreated>(`/admin/mentors/${id}/reset-password`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.mentors }),
  })
}

/**
 * Publish a rating different from the computed average.
 *
 * *** THIS NEVER OVERWRITES THE REAL SCORE ***
 * The server keeps `rating_avg` exactly as feedback produced it and stores the
 * published figure separately. Sending `rating: null` clears the override, and
 * the true average becomes visible again. Also invalidates the dashboard, whose
 * headline average is built from the same numbers.
 */
export function useSetMentorRatingOverride() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      rating,
      reason,
    }: {
      id: Uuid
      rating: string | null
      reason: string | null
    }) =>
      api.put<Mentor>(`/admin/mentors/${id}/rating-override`, { rating, reason }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.mentors })
      client.invalidateQueries({ queryKey: keys.dashboard })
    },
  })
}

// ----------------------------------------------------------- subscriptions
/**
 * Plans available to this tenant.
 *
 * Fetched for EVERY tenant, not just subscription ones: a per-session client
 * simply gets an empty list, and hiding the screen would leave an admin who
 * has just been switched to SUBSCRIPTION with nowhere to create the first plan.
 */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: keys.subscriptionPlans,
    queryFn: () => api.get<{ plans: SubscriptionPlan[] }>('/admin/subscription-plans'),
  })
}

export function useCreateSubscriptionPlan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: SubscriptionPlanInput) =>
      api.post<SubscriptionPlan>('/admin/subscription-plans', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.subscriptionPlans }),
  })
}

export function useUpdateSubscriptionPlan() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: Uuid; input: Partial<SubscriptionPlanInput> }) =>
      api.patch<SubscriptionPlan>(`/admin/subscription-plans/${id}`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.subscriptionPlans }),
  })
}

/** A student's subscription and its ledger. 404 when they have none. */
export function useStudentSubscription(studentId: Uuid | null) {
  return useQuery({
    queryKey: keys.studentSubscription(studentId ?? ''),
    enabled: Boolean(studentId),
    // A student with no plan is a normal state, not a failure to retry.
    retry: false,
    queryFn: () =>
      api.get<SubscriptionDetail>(`/admin/students/${studentId}/subscription`),
  })
}

export function useAssignSubscription() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ studentId, planId }: { studentId: Uuid; planId: Uuid }) =>
      api.post<StudentSubscription>(`/admin/students/${studentId}/subscription`, {
        plan_id: planId,
      }),
    onSuccess: (_data, vars) => {
      client.invalidateQueries({ queryKey: keys.studentSubscription(vars.studentId) })
      client.invalidateQueries({ queryKey: keys.subscriptionPlans })
    },
  })
}

// ------------------------------------------------------- cash collection
/**
 * Plan purchases waiting for an admin to confirm payment at the counter.
 *
 * *** REFUSED WITH 409 ON A GATEWAY TENANT ***
 * The queue lists subscriptions that are not yet active. On a cash client that
 * is people standing at a counter; on a gateway client it would be ABANDONED
 * CHECKOUTS, and approving one would hand out a plan nobody paid for. The
 * server refuses, and the page is hidden — `enabled` keeps it from asking at
 * all, so a gateway admin never sees an error for a screen they cannot use.
 */
export function useCashRequests(enabled = true) {
  return useQuery({
    queryKey: keys.cashRequests,
    queryFn: () => api.get<CashRequestList>('/admin/subscription-requests'),
    enabled,
    // Someone is at a counter waiting. A stale queue means a student standing
    // there while the admin looks at a list that no longer reflects reality.
    staleTime: 0,
    refetchInterval: 30_000,
  })
}

/**
 * Confirm cash was taken, and activate the plan.
 *
 * Runs the SAME activation the gateway webhook does, so the validity clock
 * starts identically. The note is the only receipt a cash payment ever has.
 */
export function useApproveCashRequest() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      subscriptionId,
      collectionNote,
    }: {
      subscriptionId: Uuid
      collectionNote: string | null
    }) =>
      api.post<StudentSubscription>(
        `/admin/subscription-requests/${subscriptionId}/approve`,
        { collection_note: collectionNote },
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.cashRequests })
      // The student now holds a live plan, and the money is recorded.
      client.invalidateQueries({ queryKey: keys.subscriptionPlans })
      client.invalidateQueries({ queryKey: ['students'] })
      client.invalidateQueries({ queryKey: ['payments'] })
      client.invalidateQueries({ queryKey: keys.dashboard })
    },
  })
}

/** Turn a request down. The reason is required and kept in the student's history. */
export function useRejectCashRequest() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      subscriptionId,
      reason,
    }: {
      subscriptionId: Uuid
      reason: string
    }) =>
      api.post<{ message: string }>(
        `/admin/subscription-requests/${subscriptionId}/reject`,
        { reason },
      ),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.cashRequests })
      client.invalidateQueries({ queryKey: ['payments'] })
    },
  })
}

// ---------------------------------------------------------------- students
export interface StudentFilters {
  page?: number
  page_size?: number
  search?: string
  status?: string
  category_id?: string
}

export function useStudents(filters: StudentFilters) {
  return useQuery({
    queryKey: keys.students(filters),
    queryFn: () => api.get<StudentList>('/admin/students', { ...filters }),
    // Keeps the previous page visible while the next loads, so the table does
    // not collapse to a skeleton on every page change.
    placeholderData: (previous) => previous,
  })
}

export function useStudentDetail(id: Uuid | null) {
  return useQuery({
    queryKey: keys.student(id ?? ''),
    queryFn: () => api.get<StudentDetail>(`/admin/students/${id}`),
    enabled: id !== null,
  })
}

// ---------------------------------------------------------------- bookings
export interface BookingFilters {
  page?: number
  page_size?: number
  status?: string
  mentor_id?: string
  student_id?: string
  date_from?: string
  date_to?: string
  search?: string
}

export function useBookings(filters: BookingFilters) {
  return useQuery({
    queryKey: keys.bookings(filters),
    queryFn: () => api.get<BookingList>('/admin/bookings', { ...filters }),
    placeholderData: (previous) => previous,
  })
}

// ---------------------------------------------------------------- payments
export interface PaymentFilters {
  page?: number
  page_size?: number
  status?: string
  date_from?: string
  date_to?: string
  search?: string
}

export function usePayments(filters: PaymentFilters) {
  return useQuery({
    queryKey: keys.payments(filters),
    queryFn: () => api.get<PaymentList>('/admin/payments', { ...filters }),
    placeholderData: (previous) => previous,
  })
}

// ---------------------------------------------------------------- feedback
export interface FeedbackFilters {
  page?: number
  page_size?: number
  mentor_id?: string
  min_rating?: number
  max_rating?: number
}

export function useFeedback(filters: FeedbackFilters) {
  return useQuery({
    queryKey: keys.feedback(filters),
    queryFn: () => api.get<FeedbackList>('/admin/feedback', { ...filters }),
    placeholderData: (previous) => previous,
  })
}

// ------------------------------------------------------------ chat threads
export function useChatThreads(filters: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: keys.chatThreads(filters),
    queryFn: () => api.get<ChatOverview>('/admin/chat-threads', { ...filters }),
    placeholderData: (previous) => previous,
  })
}

// ---------------------------------------------------------- admin accounts
/**
 * Who else can sign in to this portal.
 *
 * Tenant-scoped server-side; there is no way to ask for another institute's
 * staff.
 */
export function useAdmins(enabled = true) {
  return useQuery({
    queryKey: keys.admins,
    queryFn: () => api.get<AdminAccountList>('/admin/admins'),
    enabled,
  })
}

/**
 * Set a colleague's password to one the caller has chosen.
 *
 * *** THE PASSWORD IS SENT, NEVER RETURNED ***
 * The caller typed it, so echoing it back would only put a live credential
 * into a response the browser may cache.
 */
export function useResetAdminPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: Uuid; newPassword: string }) =>
      api.post<{ message: string }>(`/admin/admins/${userId}/reset-password`, {
        new_password: newPassword,
      }),
    // The lockout is cleared by a reset, so the list is stale afterwards.
    onSuccess: () => client.invalidateQueries({ queryKey: keys.admins }),
  })
}

// ------------------------------------------------------------ staff accounts
/**
 * Add a colleague to this institute's portal.
 *
 * *** ADMIN-ONLY, AND THE SERVER IS WHAT ENFORCES IT ***
 * A Coordinator or Accountant is refused by the area gate before the handler
 * runs — whoever can create an account can create a full ADMIN and hand it to
 * themselves. The screen hiding itself is a courtesy, never the control.
 *
 * The generated password comes back ONCE and is never fetchable again.
 */
export function useCreateAdminAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAdminInput) =>
      api.post<CreatedAdminAccount>('/admin/admins', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.admins }),
  })
}

/**
 * Remove a colleague's access.
 *
 * *** SOFT: THE ACCOUNT STOPS WORKING, THE HISTORY STAYS ***
 * Audit rows and cash approvals point at this user, so the row is kept and
 * simply stops appearing. The server refuses two cases — deactivating
 * yourself, and removing the last administrator — because an institute with
 * no administrator cannot add one back without us.
 */
export function useDeactivateAdmin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: Uuid) =>
      api.delete<{ message: string }>(`/admin/admins/${userId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.admins }),
  })
}

// ---------------------------------------------------------------- settings
export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<TenantSettings>('/admin/settings'),
  })
}

/**
 * Sessions already booked on a weekday, before closing it.
 *
 * *** A PREVIEW. IT CHANGES NOTHING. ***
 * Closing a day never cancels a booking — this exists so an admin knows who
 * is affected rather than finding out when a mentor works a day the
 * organisation is officially closed.
 *
 * Fetched only when `weekday` is set, i.e. when a day is actually being
 * turned off.
 */
export function useOperatingDayImpact(weekday: number | null) {
  return useQuery({
    queryKey: keys.operatingDayImpact(weekday ?? -1),
    queryFn: () =>
      api.get<OperatingDayImpact>('/admin/settings/operating-days/impact', {
        weekday: weekday as number,
      }),
    enabled: weekday !== null,
    // Always re-checked: a booking made since the last look would otherwise
    // be missing from a warning the admin is about to act on.
    staleTime: 0,
  })
}

export function useUpdateSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: TenantSettingsInput) =>
      api.put<TenantSettings>('/admin/settings', input),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.settings }),
  })
}

// ----------------------------------------------------------------- storage
export function useStorageUsage() {
  return useQuery({
    queryKey: keys.storage,
    queryFn: () => api.get<StorageUsage>('/admin/storage'),
    // Walks the filesystem server-side; no need to refresh it often.
    staleTime: 5 * 60_000,
  })
}

/* ------------------------------------------------------------- libraries */

/**
 * The library list, with each card's state already decided by the server.
 *
 * *** POLLED, BECAUSE OCCUPANCY IS SHARED STATE ***
 * Seats fill while this screen is open. A stale count would offer Join on a
 * room that filled thirty seconds ago, and the student would be refused for
 * no reason they can see. Ten seconds is frequent enough that the badge is
 * believable and far cheaper than a websocket for a list this small.
 */
export function useLibraries() {
  return useQuery({
    queryKey: keys.libraries,
    queryFn: () => api.get<LibraryCardList>('/libraries'),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Who is in one library right now.
 *
 * *** ONLY FOR THE ROOM THEY ARE IN ***
 * Fetched per library, not per card: polling every library on the list would
 * be one request per room every few seconds, and a student only needs to see
 * the people they are actually sitting with.
 *
 * The interval is deliberately slower than the heartbeat. Arrivals are not
 * urgent — nobody is waiting on a name to appear — and a long study session
 * should not spend an hour making requests nobody reads.
 */
const OCCUPANTS_REFETCH_MS = 60_000

export function useLibraryOccupants(libraryId: string | null) {
  return useQuery({
    queryKey: [...keys.libraries, libraryId, 'occupants'],
    queryFn: () =>
      api.get<LibraryOccupantList>(`/libraries/${libraryId}/occupants`),
    enabled: libraryId !== null,
    refetchInterval: OCCUPANTS_REFETCH_MS,
    // A stale name list is harmless; a flash of empty state while refetching
    // reads as "everyone left".
    placeholderData: (prev) => prev,
  })
}

/**
 * The library room conversation.
 *
 * *** POLLED, NOT PUSHED ***
 * A websocket would be the right answer for a busy chat. This room has a
 * handful of people in it and already has a heartbeat timer running, so a
 * second poll is far less machinery than a socket to keep alive, reconnect
 * and authenticate. Revisit if rooms get large.
 */
const MESSAGES_REFETCH_MS = 8_000

export function useLibraryMessages(libraryId: string | null) {
  return useQuery({
    queryKey: [...keys.libraries, libraryId, 'messages'],
    queryFn: () =>
      api.get<RoomMessageList>(`/libraries/${libraryId}/messages`),
    enabled: libraryId !== null,
    refetchInterval: MESSAGES_REFETCH_MS,
    // A flash of empty while refetching reads as "the chat was wiped".
    placeholderData: (prev) => prev,
  })
}

export function usePostLibraryMessage(libraryId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api.post<RoomMessage>(`/libraries/${libraryId}/messages`, { body }),
    onSuccess: (message) => {
      /*
        *** APPENDED, NOT REFETCHED ***
        Waiting up to 8 seconds to see your own message appear reads as a
        send that failed. The poll will converge on the same list anyway;
        this only makes the sender's own line immediate.
      */
      client.setQueryData<RoomMessageList>(
        [...keys.libraries, libraryId, 'messages'],
        (prev) =>
          prev
            ? // Guard against the poll having already included it.
              prev.messages.some((m) => m.message_id === message.message_id)
              ? prev
              : { messages: [...prev.messages, message] }
            : { messages: [message] },
      )
    },
  })
}

/**
 * The study-hours board.
 *
 * Slower than the chat: a ranking that reshuffles every few seconds is
 * distracting in a room meant for concentration, and nothing on it changes
 * faster than the minute it is measured in.
 */
const BOARD_REFETCH_MS = 60_000

export function useLeaderboard(period: LeaderboardPeriod) {
  return useQuery({
    queryKey: [...keys.leaderboard, period],
    queryFn: () =>
      api.get<Leaderboard>(`/library/leaderboard?period=${period}`),
    refetchInterval: BOARD_REFETCH_MS,
    placeholderData: (prev) => prev,
  })
}

export function useMyProgress() {
  return useQuery({
    queryKey: [...keys.leaderboard, 'me'],
    queryFn: () => api.get<MyProgress>('/library/my-progress'),
    refetchInterval: BOARD_REFETCH_MS,
  })
}

/**
 * One occupant's card: totals, streak and badges.
 *
 * Fetched only when a tile is opened, not with the occupant list — a room
 * of forty would otherwise be forty badge queries nobody asked for.
 */
export function usePersonCard(libraryId: string, userId: string | null) {
  return useQuery({
    queryKey: [...keys.libraries, libraryId, 'person', userId],
    queryFn: () =>
      api.get<PersonCard>(`/libraries/${libraryId}/occupants/${userId}`),
    enabled: userId !== null,
    // Study history barely moves while a card is open.
    staleTime: 60_000,
  })
}

export function useJoinLibrary() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ libraryId, switchFrom }: { libraryId: string; switchFrom?: boolean }) =>
      api.post<LibraryVisit>(
        `/libraries/${libraryId}/join${switchFrom ? '?switch=true' : ''}`,
        {},
      ),
    /*
      Invalidate on BOTH outcomes. A refusal is usually "this filled up" or
      "you are already somewhere" — in either case the cached list is what
      misled the student into trying, so it must be refreshed even though the
      request failed.
    */
    /*
      *** AWAITED, NOT FIRE-AND-FORGET ***
      `invalidateQueries` marks the list stale and returns immediately. The
      lobby then navigated into the room while the cache still said
      `i_am_inside: false`, the room's own guard read that, and threw the
      student straight back out with "you are no longer in this room" —
      so entering took two clicks. Returning the promise makes
      `mutateAsync` resolve only once the fresh list has arrived.
    */
    onSettled: () =>
      client.invalidateQueries({ queryKey: keys.libraries }),
  })
}

/**
 * Step away for thirty minutes.
 *
 * *** THE SEAT AND THE STREAK SURVIVE; THE CLOCK DOES NOT ***
 * Study time stops the moment this is called. The library sells verified
 * study hours, and a break is not studying.
 */
export function useStartBreak() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<LibraryVisit>('/libraries/break', {}),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.libraries })
    },
  })
}

export function useEndBreak() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<LibraryVisit>('/libraries/break'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.libraries })
    },
  })
}

export function useLeaveLibrary() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<LibraryLeaveResult>('/libraries/leave', {}),
    onSettled: () => {
      client.invalidateQueries({ queryKey: keys.libraries })
    },
  })
}

/**
 * One heartbeat. Deliberately NOT a React Query mutation.
 *
 * *** IT MUST NOT INVALIDATE ANYTHING ***
 * A heartbeat fires every thirty seconds for hours. Refetching the library
 * list each time would be a self-inflicted poll, and a failed heartbeat must
 * not surface as a toast — the student is studying, not operating the app.
 * The caller swallows errors and lets the next one try.
 */
export function sendLibraryHeartbeat() {
  return api.post<LibraryVisit>('/libraries/heartbeat', {})
}

/* ------------------------------------------- libraries: administration */

export function useAdminLibraries() {
  return useQuery({
    queryKey: keys.adminLibraries,
    queryFn: () => api.get<AdminLibraryList>('/admin/libraries'),
    /*
      Occupancy is live on this screen too — an admin closing a room wants to
      know whether anyone is in it. Slower than the student poll: nobody is
      waiting on a seat here.
    */
    refetchInterval: 30_000,
  })
}

export function useCreateLibrary() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: LibraryInput) => api.post('/admin/libraries', input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.adminLibraries })
      // The student-facing list is a different query and would otherwise
      // keep showing the old set until its own poll came round.
      client.invalidateQueries({ queryKey: keys.libraries })
    },
  })
}

export function useUpdateLibrary() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<LibraryInput> }) =>
      api.patch(`/admin/libraries/${id}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.adminLibraries })
      client.invalidateQueries({ queryKey: keys.libraries })
    },
  })
}

export function useCloseLibrary() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/libraries/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.adminLibraries })
      client.invalidateQueries({ queryKey: keys.libraries })
    },
  })
}

export function useLibraryMentors(libraryId: string | null) {
  return useQuery({
    queryKey: [...keys.adminLibraries, libraryId, 'mentors'],
    queryFn: () =>
      api.get<LibraryMentorGrantList>(`/admin/libraries/${libraryId}/mentors`),
    enabled: libraryId !== null,
  })
}

export function useGrantLibraryMentor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ libraryId, mentorId }: { libraryId: string; mentorId: string }) =>
      api.post(`/admin/libraries/${libraryId}/mentors`, { mentor_id: mentorId }),
    onSuccess: (_data, { libraryId }) => {
      client.invalidateQueries({
        queryKey: [...keys.adminLibraries, libraryId, 'mentors'],
      })
    },
  })
}

export function useRevokeLibraryMentor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ libraryId, mentorId }: { libraryId: string; mentorId: string }) =>
      api.delete(`/admin/libraries/${libraryId}/mentors/${mentorId}`),
    onSuccess: (_data, { libraryId }) => {
      client.invalidateQueries({
        queryKey: [...keys.adminLibraries, libraryId, 'mentors'],
      })
    },
  })
}

/* ===================================================================== */
/* MY AVAILABILITY — a mentor's own diary                                */
/*                                                                       */
/* `/mentor/*`, the mentor acting on themselves. The admin equivalents   */
/* under `/admin/mentors/{id}/...` are a different question asked by a   */
/* different person.                                                     */
/* ===================================================================== */

export function useMyWorkingHours(enabled = true) {
  return useQuery({
    queryKey: keys.myWorkingHours,
    queryFn: () => api.get<WorkingHours>('/mentor/working-hours'),
    enabled,
  })
}

export function useSetMyWorkingHours() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (entries: WorkingHoursEntry[]) =>
      api.put<WorkingHours>('/mentor/working-hours', { entries }),
    onSuccess: (data) => {
      client.setQueryData(keys.myWorkingHours, data)
      /*
        Slots are generated from these, so every availability answer the
        booking funnel holds is now stale.
      */
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

export function useMyBreaks(enabled = true) {
  return useQuery({
    queryKey: keys.myBreaks,
    queryFn: () => api.get<Breaks>('/mentor/breaks'),
    enabled,
  })
}

export function useSetMyBreaks() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (breaks: BreakEntry[]) =>
      api.put<Breaks>('/mentor/breaks', { breaks }),
    onSuccess: (data) => {
      client.setQueryData(keys.myBreaks, data)
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

export function useMyOneOffBreaks(enabled = true) {
  return useQuery({
    queryKey: keys.myOneOffBreaks,
    queryFn: () => api.get<OneOffBreaks>('/mentor/one-off-breaks'),
    enabled,
  })
}

export function useAddOneOffBreak() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      break_date: string
      start_time: string
      end_time: string
      label?: string
    }) => api.post<OneOffBreak>('/mentor/one-off-breaks', body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.myOneOffBreaks })
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

export function useRemoveOneOffBreak() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (breakId: Uuid) =>
      api.delete<{ message: string }>(`/mentor/one-off-breaks/${breakId}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.myOneOffBreaks })
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

export function useMyVacations(enabled = true) {
  return useQuery({
    queryKey: keys.myVacations,
    queryFn: () => api.get<Vacations>('/mentor/vacations'),
    enabled,
  })
}

export function useAddVacation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      start_date: string
      end_date: string
      reason?: string
    }) => api.post<{ message: string }>('/mentor/vacations', body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.myVacations })
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

export function useRemoveVacation() {
  const client = useQueryClient()
  return useMutation({
    /*
      Identified by RANGE, not by id — the server stores one row per day
      and groups them for display, so there is no single id to delete.
      Query parameters, which is what the endpoint takes.
    */
    mutationFn: (params: { start_date: string; end_date: string }) =>
      api.delete<{ message: string }>('/mentor/vacations', params),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.myVacations })
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

/**
 * What a proposed break would clash with.
 *
 * *** A PREVIEW. IT CHANGES NOTHING. ***
 * Adding availability never voids a settled booking on its own. The mentor
 * sees the clashes and decides about them separately, which is why
 * cancelling is its own endpoint and its own confirmation.
 *
 * Omit the times for a whole-day range (a vacation); pass them for a
 * partial-day break.
 */
export function useAffectedBookings(
  range: {
    start_date: string
    end_date: string
    start_time?: string
    end_time?: string
  } | null,
) {
  return useQuery({
    queryKey: keys.affectedBookings(range),
    queryFn: () =>
      api.get<AffectedBookings>('/mentor/availability/affected-bookings', {
        ...range,
      }),
    enabled: range !== null,
    /* A preview of right now. Never served from an older answer. */
    staleTime: 0,
    gcTime: 0,
  })
}

export function useCancelAffectedBookings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (bookingIds: Uuid[]) =>
      api.post<CancelBookingsResult>('/mentor/availability/cancel-bookings', {
        booking_ids: bookingIds,
      }),
    onSuccess: () => {
      // The mentor's own list, and the admin table if they can see it.
      client.invalidateQueries({ queryKey: ['my-bookings'] })
      client.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}

/* ===================================================================== */
/* MY PROFILE — what the caller may change about themselves              */
/*                                                                       */
/* Two endpoints, because the two roles hold different things: a student */
/* has a category and a verified email, a mentor has a bio and a         */
/* visibility rule. One merged type would be mostly nulls.               */
/* ===================================================================== */

export function useMyStudentProfile(enabled = true) {
  return useQuery({
    queryKey: keys.myStudentProfile,
    queryFn: () => api.get<MyStudentProfile>('/me/profile'),
    enabled,
  })
}

export function useUpdateMyName() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (fullName: string) =>
      api.put<MyStudentProfile>('/me/profile', { full_name: fullName }),
    onSuccess: (data) => {
      // Write the answer straight in — the server returned the whole
      // profile, so a refetch would ask for what we already hold.
      client.setQueryData(keys.myStudentProfile, data)
    },
  })
}

/**
 * Change the email, which sends a code to the NEW address.
 *
 * *** THE NEW ADDRESS IS UNVERIFIED UNTIL CONFIRMED ***
 * Booking is gated on `email_verified`, so this costs the student access
 * until they enter the code. The server refuses it outright while an
 * upcoming session exists.
 */
export function useChangeMyEmail() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (email: string) =>
      api.put<MyStudentProfile>('/me/profile/email', { email }),
    onSuccess: (data) => {
      client.setQueryData(keys.myStudentProfile, data)
      // The session carries the email, and the header shows it.
      client.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

/**
 * Subject areas the student can choose from.
 *
 * Active ones only — the server filters. A deactivated category stays
 * valid for students already in it but must not be selectable.
 */
export function usePublicCategories(enabled = true) {
  return useQuery({
    queryKey: keys.publicCategories,
    queryFn: () => api.get<PublicCategoryList>('/categories'),
    enabled,
    /* An admin edits these rarely. */
    staleTime: 60_000,
  })
}

/**
 * Set the student's subject area.
 *
 * *** EXISTING BOOKINGS ARE UNAFFECTED ***
 * The server is explicit: a student who switches keeps every session they
 * already booked, because those are commitments rather than preferences.
 * Only discovery is re-scoped.
 */
export function useSelectCategory() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (categoryId: Uuid) =>
      api.put<PublicCategory>('/me/category', { category_id: categoryId }),
    onSuccess: () => {
      // The profile carries the chosen category…
      client.invalidateQueries({ queryKey: keys.myStudentProfile })
      // …and the whole booking funnel is scoped to it.
      client.invalidateQueries({ queryKey: ['public-mentors'] })
      client.invalidateQueries({ queryKey: ['mentor-slots'] })
      client.invalidateQueries({ queryKey: ['available-dates'] })
    },
  })
}

export function useMyMentorProfile(enabled = true) {
  return useQuery({
    queryKey: keys.myMentorProfile,
    queryFn: () => api.get<MyMentorProfile>('/mentor/profile'),
    enabled,
  })
}

export function useUpdateMyMentorProfile() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: MentorProfileUpdate) =>
      api.put<MyMentorProfile>('/mentor/profile', body),
    onSuccess: (data) => {
      client.setQueryData(keys.myMentorProfile, data)
      /*
        Supplying title AND bio is what flips `is_visible`, which changes
        whether this mentor appears to students at all.
      */
      client.invalidateQueries({ queryKey: ['public-mentors'] })
    },
  })
}

/**
 * Change your own password.
 *
 * Mentors only: the endpoint lives under `/mentor`, and an admin changes
 * theirs through the admin accounts screen.
 */
export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      api.post<{ message: string }>('/mentor/change-password', body),
  })
}

/**
 * Who is asking, for cache keys that differ by caller.
 *
 * *** SOME ANSWERS ARE ABOUT THE VIEWER, NOT THE RESOURCE ***
 * A chat message's `is_mine` and a thread's `is_locked` are computed from
 * the caller's own id. Two people reading the SAME thread get genuinely
 * different payloads, so the thread id alone does not identify the data.
 *
 * Falls back to a constant when signed out — no query runs then anyway.
 */
function useViewerId(): string {
  const { user } = useAuth()
  return user?.user_id ?? 'anon'
}

/* ===================================================================== */
/* CHAT — the caller's own conversations                                 */
/*                                                                       */
/* `/chat/threads` serves BOTH roles. `chatThreads` above is the ADMIN   */
/* table of every conversation in the tenant and stays separate.         */
/* ===================================================================== */

export function useMyThreads() {
  const viewer = useViewerId()
  return useQuery({
    queryKey: keys.myThreads(viewer),
    queryFn: () => api.get<ChatThreadList>('/chat/threads'),
    /*
      There is no socket. A conversation list that only updated on
      navigation would leave someone staring at a stale unread count while
      the other party is typing.
    */
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * A page of message history, newest first.
 *
 * *** THE WHOLE THREAD, NOT ONE PAGE ***
 * `useInfiniteQuery` with `before_id` would be the natural fit, but the
 * server also wants `after_id` polling for new messages on the SAME list —
 * two cursors moving in opposite directions over one cache entry. That is
 * what `ChatPage` manages itself; this hook fetches the newest page and the
 * component appends older ones.
 */
export function useThreadMessages(threadId: Uuid | undefined) {
  const viewer = useViewerId()
  return useQuery({
    queryKey: keys.threadMessages(viewer, threadId ?? 'none'),
    queryFn: () =>
      api.get<ChatMessageList>(`/chat/threads/${threadId}/messages`, {
        limit: 50,
      }),
    enabled: Boolean(threadId),
    /*
      Polling the newest page is how new messages arrive. `after_id` would
      be cheaper, but it returns ONLY new rows — merging those into a cache
      that may have been invalidated is how a message goes missing. Fifty
      rows every ten seconds is affordable and cannot lose one.
    */
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * Whether this viewer may send.
 *
 * Advisory — the server re-checks on every send. Fetched separately from
 * the thread list because a lock can lapse mid-conversation.
 */
/**
 * One page of OLDER messages, fetched on demand.
 *
 * *** DELIBERATELY NOT A QUERY ***
 * `useThreadMessages` polls the newest page every ten seconds. Older pages
 * are fixed history that never changes, and merging a polled list with
 * accumulated pages in one cache entry is how a message goes missing —
 * a refetch replaces the array and the older pages vanish, or a merge
 * duplicates a row that arrived mid-scroll.
 *
 * So this is a plain call. The component keeps what it has loaded, and the
 * poll keeps owning the newest page alone.
 *
 * `beforeId` is a keyset cursor anchored to a row, so messages arriving
 * while someone scrolls back cannot shift the window.
 */
export async function fetchOlderMessages(
  threadId: Uuid,
  beforeId: number,
): Promise<ChatMessageList> {
  return api.get<ChatMessageList>(`/chat/threads/${threadId}/messages`, {
    before_id: beforeId,
    limit: 50,
  })
}

export function useThreadQuota(threadId: Uuid | undefined) {
  const viewer = useViewerId()
  return useQuery({
    queryKey: keys.threadQuota(viewer, threadId ?? 'none'),
    queryFn: () => api.get<ChatQuota>(`/chat/threads/${threadId}/quota`),
    enabled: Boolean(threadId),
  })
}

export function useSendMessage(threadId: Uuid | undefined) {
  const client = useQueryClient()
  const viewer = useViewerId()
  return useMutation({
    mutationFn: (body: { body?: string; attachment_id?: Uuid }) =>
      api.post<ChatMessage>(`/chat/threads/${threadId}/messages`, body),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: keys.threadMessages(viewer, threadId ?? 'none'),
        }),
        // The quota moved — a free-message allowance counts down.
        client.invalidateQueries({
          queryKey: keys.threadQuota(viewer, threadId ?? 'none'),
        }),
        // And the list's last-message time and ordering.
        client.invalidateQueries({ queryKey: keys.myThreads(viewer) }),
      ])
    },
  })
}

/**
 * Mark everything in a thread read.
 *
 * Invalidates the LIST, not the messages: the unread badge is what changes
 * for the viewer, and refetching the open conversation would scroll it.
 */
export function useMarkThreadRead() {
  const client = useQueryClient()
  const viewer = useViewerId()
  return useMutation({
    mutationFn: (threadId: Uuid) =>
      api.post<{ marked_read: number }>(`/chat/threads/${threadId}/read`, {}),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.myThreads(viewer) })
    },
  })
}

/* ===================================================================== */
/* MY PLAN — the student's own subscription                              */
/*                                                                       */
/* `/me/subscription` and `/me/plans`, NOT the admin `/subscription-     */
/* plans` above. The student asks "what do I hold and what may I buy";   */
/* the admin asks "what has this tenant sold".                           */
/* ===================================================================== */

export function useMySubscription() {
  return useQuery({
    queryKey: keys.mySubscription,
    queryFn: () => api.get<MySubscription>('/me/subscription'),
    /*
      A plan goes live on a webhook the browser never sees, so a student
      who paid in another tab would otherwise sit on a stale balance.
    */
    refetchOnWindowFocus: true,
    /*
      *** KEEP ASKING WHILE A PAYMENT IS IN FLIGHT ***
      A student who returns from the bank lands back on this page with the
      purchase still settling. Without this they would see "Payment
      pending" until they reloaded — having just paid, which is exactly
      when a stale screen is least forgivable.

      PENDING only. A FAILED payment is a settled answer and polling it
      would never change anything.

      Fifteen seconds matches the waiting screen's slow phase; the fast
      three-second poll belongs there, on the screen someone is watching.
    */
    refetchInterval: (query) =>
      query.state.data?.last_payment_status === 'PENDING' ? 15_000 : false,
  })
}

/**
 * Every plan this student has held.
 *
 * Separate from `useMySubscription`, which reports only the newest row
 * because it answers "what can I do right now".
 */
export function useMySubscriptionHistory() {
  return useQuery({
    queryKey: keys.mySubscriptionHistory,
    queryFn: () => api.get<SubscriptionHistory>('/me/subscriptions'),
    /* History changes only when a plan is bought or ends. */
    staleTime: 60_000,
  })
}

export function useMyPlans() {
  return useQuery({
    queryKey: keys.myPlans,
    queryFn: () => api.get<PublicPlanList>('/me/plans'),
    /* Plans change when an admin edits them, which is rare. */
    staleTime: 60_000,
  })
}

/**
 * Start buying a plan.
 *
 * *** DELIBERATELY DOES NOT INVALIDATE ANYTHING ***
 * Nothing has been paid when this resolves — the subscription is created
 * dormant and activates only on the gateway's signed webhook. Refetching
 * here would show the student the same "no plan" state they started from
 * and read as a failure. The waiting screen polls instead.
 */
export function usePurchasePlan() {
  return useMutation({
    mutationFn: (planId: Uuid) =>
      api.post<PurchaseStarted>('/me/subscription/purchase', {
        plan_id: planId,
      }),
  })
}

/* ===================================================================== */
/* MEETINGS — the caller's own sessions                                  */
/*                                                                       */
/* `/bookings` serves BOTH roles: a student sees what they booked, a     */
/* mentor sees what was booked with them. One endpoint, one hook, and    */
/* the server decides which rows belong to whom.                         */
/* ===================================================================== */

export function useMyBookings(upcoming: boolean) {
  return useQuery({
    queryKey: keys.myBookings(upcoming),
    queryFn: () =>
      api.get<MyBookingList>('/bookings', { upcoming, limit: 50 }),
    /*
      A session becomes joinable at a wall-clock moment the server decides
      (`can_join_from`). Without a poll, someone sitting on this screen
      watches a disabled button that never enables until they reload.
    */
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export function useMyBooking(bookingId: Uuid | undefined) {
  return useQuery({
    queryKey: keys.myBooking(bookingId ?? 'none'),
    queryFn: () => api.get<MyBooking>(`/bookings/${bookingId}`),
    enabled: Boolean(bookingId),
  })
}

/**
 * What cancelling would cost, before the user commits.
 *
 * Only fetched when a dialog is actually open: it is a per-booking
 * calculation and prefetching it for a whole list would be one request per
 * row for a dialog most people never open.
 */
export function useCancellationPreview(bookingId: Uuid | null) {
  return useQuery({
    queryKey: keys.cancellationPreview(bookingId ?? 'none'),
    queryFn: () =>
      api.get<CancellationPreview>(
        `/bookings/${bookingId}/cancellation-preview`,
      ),
    enabled: Boolean(bookingId),
    /*
      `hours_until_session` and the refund both move with the clock, and a
      cached answer from an hour ago can offer a refund the cutoff has
      since passed. Always refetch.
    */
    staleTime: 0,
  })
}

export function useCancelBooking() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: Uuid; reason?: string }) =>
      api.post<MyBooking>(`/bookings/${bookingId}/cancel`, {
        reason: reason || undefined,
      }),
    /*
      Awaited for the same reason the library join is: the list must carry
      the new status before the dialog closes, or the row still reads
      CONFIRMED and offers Cancel again.

      Both lists, not just the upcoming one — a cancelled session leaves
      "upcoming" and appears under past.
    */
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['my-bookings'] }),
        // A refund or a returned plan session changes what the student
        // may buy next.
        client.invalidateQueries({ queryKey: ['subscription'] }),
      ])
    },
  })
}

/* ===================================================================== */
/* BOOKING A SESSION — the student funnel                                */
/* ===================================================================== */

/**
 * Mentors a student may book.
 *
 * *** THE SERVER PICKS THE CATEGORY, NOT THIS CALL ***
 * `/mentors` returns the mentors in the STUDENT'S OWN category, read from
 * their profile. A student without one gets a 404 telling them to choose —
 * which the screen turns into an action rather than an error.
 */
export function usePublicMentors() {
  return useQuery({
    queryKey: ['public-mentors'],
    queryFn: () => api.get<PublicMentorList>('/mentors'),
    // Mentors and their photos change rarely; availability does not live here.
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function usePublicSessionTypes() {
  return useQuery({
    queryKey: ['public-session-types'],
    queryFn: () => api.get<PublicSessionTypeList>('/session-types'),
    staleTime: 5 * 60_000,
  })
}

/** Which days this mentor has slots on, as counts. One request, not one
 *  per day. */
export function useAvailableDates(
  mentorId: Uuid | undefined,
  sessionTypeId: Uuid | undefined,
  days = 21,
) {
  return useQuery({
    queryKey: ['available-dates', mentorId, sessionTypeId, days],
    queryFn: () =>
      api.get<AvailableDates>(`/mentors/${mentorId}/available-dates`, {
        session_type_id: sessionTypeId,
        days,
      }),
    enabled: Boolean(mentorId && sessionTypeId),
  })
}

export function useMentorSlots(
  mentorId: Uuid | undefined,
  sessionTypeId: Uuid | undefined,
  date: string | null,
) {
  return useQuery({
    queryKey: ['mentor-slots', mentorId, sessionTypeId, date],
    queryFn: () =>
      api.get<SlotList>(`/mentors/${mentorId}/availability`, {
        session_type_id: sessionTypeId,
        date,
      }),
    enabled: Boolean(mentorId && sessionTypeId && date),
    /*
      Someone else can take a slot while this screen is open. Short staleness
      so a returning tab re-checks rather than booking into a gap that
      closed — the server refuses it anyway, but the refusal is confusing
      when the slot was visibly there.
    */
    staleTime: 30_000,
  })
}

/**
 * What this session would cost.
 *
 * Advisory: `create_booking` recomputes and snapshots the price, so a stale
 * quote can never become the charged amount.
 */
export function usePriceQuote(
  mentorId: Uuid | undefined,
  sessionTypeId: Uuid | undefined,
) {
  return useQuery({
    queryKey: ['price-quote', mentorId, sessionTypeId],
    queryFn: () =>
      api.get<PriceQuote>(`/mentors/${mentorId}/quote`, {
        session_type_id: sessionTypeId,
      }),
    enabled: Boolean(mentorId && sessionTypeId),
    retry: false,
  })
}

/**
 * Start paying for a booking that is PENDING_PAYMENT.
 *
 * *** ONLY ON A PER-SESSION TENANT ***
 * A subscription booking is CONFIRMED the moment it is made — the plan IS
 * the payment, and asking for money again would charge twice.
 *
 * *** DELIBERATELY INVALIDATES NOTHING ***
 * Nothing is paid when this resolves. The booking is confirmed by a
 * signature-verified webhook, and refetching here would show the student
 * the same PENDING_PAYMENT row they started from.
 */
export function usePayForBooking() {
  return useMutation({
    mutationFn: (bookingId: Uuid) =>
      api.post<PaymentIntent>(`/bookings/${bookingId}/pay`, {}),
  })
}

/**
 * Rate a finished session.
 *
 * *** THE RATING IS RECOMPUTED IN SQL, NOT INCREMENTED ***
 * The server runs `sp_recalculate_mentor_rating` so the average always
 * reflects every row. That changes `rating_avg` on the mentor's card, so
 * the discovery caches go too.
 */
export function useRateSession() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      bookingId,
      ...body
    }: FeedbackInput & { bookingId: Uuid }) =>
      api.post<Feedback>(`/bookings/${bookingId}/feedback`, body),
    onSuccess: () => {
      // `my_rating` on the booking row is how the list knows it is rated.
      client.invalidateQueries({ queryKey: ['my-bookings'] })
      client.invalidateQueries({ queryKey: ['public-mentors'] })
    },
  })
}

export function useCreateBooking() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      mentor_id: Uuid
      session_type_id: Uuid
      slot_start_utc: string
      idempotency_key?: string
    }) => api.post<MyBooking>('/bookings', body),
    /*
      *** NO DURATION IS SENT ***
      The server regenerates the slot from the start time. Trusting a client
      duration would let a crafted request book a long session at a short
      session's price — which is why the request type has no such field.
    */
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['my-bookings'] }),
        // The slot is gone now, and a plan may have one fewer session.
        client.invalidateQueries({ queryKey: ['mentor-slots'] }),
        client.invalidateQueries({ queryKey: ['available-dates'] }),
        client.invalidateQueries({ queryKey: ['subscription'] }),
      ])
    },
  })
}
